/**
 * Agent Context — thin shim over React Query.
 *
 * Server state (the agent list + per-action mutations) lives in
 * `./hooks/useAgentsQuery` and `./hooks/useAgentMutations`. This provider
 * composes those hooks behind the legacy `AgentContextType` surface so
 * existing consumers don't move.
 *
 * The `setTimeout(initializeAgents, 100)` race-guard the legacy code used
 * to "wait for auth token in localStorage" is gone — `useAgentsQuery`'s
 * `enabled: !!user` is the React Query idiom for that and fires the query
 * exactly when auth is ready, no arbitrary wait.
 *
 * `selectedAgent` stays as local `useState` because selection is a UI
 * concern, not server state.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  Agent,
  AgentContextType,
  AgentInstallRequest,
} from '@/types/agent.types';
import type { ExportEnvelope } from '@/services/agentExportImport/schema';
import { useQueryClient } from '@tanstack/react-query';
import { useAdmin } from '@/context/AdminContext';
import { useAuth } from '@/context/AuthContext';
import { agentService } from '@/services/agentService';
import { setTelemetryContext } from '@/services/errorTelemetry';
import { agentSupportsVision } from '@/utils/agentCapabilities';
import { useAgentsQuery, AGENTS_QUERY_KEY } from './hooks/useAgentsQuery';
import {
  useActivateAgent,
  useDeactivateAgent,
  useDeleteAgent,
  useDuplicateAgent,
  useImportAgent,
  useInstallAgent,
  useSetDefaultAgent,
  useUpdateAgent,
} from './hooks/useAgentMutations';

const AgentContext = createContext<AgentContextType | undefined>(undefined);

interface AgentProviderProps {
  children: ReactNode;
}

export function AgentProvider({ children }: AgentProviderProps) {
  const { config } = useAdmin();
  const { user } = useAuth();

  // ---- Server state (React Query) ----
  const agentsQuery = useAgentsQuery();

  // ---- Local mirror of agents ----
  // React Query is the source of truth, but mutations need their effects
  // to be observable synchronously in tests (`await act(mutate) → assert
  // agents.contains(...)` — the legacy contract). React Query's observer
  // notifications schedule a microtask outside of `act()`'s flush window,
  // so reading directly from `agentsQuery.data` lags one tick. Mirror it
  // in local useState and update the mirror synchronously in each
  // mutation wrapper.
  const [agents, setAgents] = useState<Agent[]>([]);
  // Sync the mirror from React Query whenever the canonical data
  // changes (initial load, refetch, external invalidation).
  useEffect(() => {
    if (agentsQuery.data) {
      setAgents(agentsQuery.data);
    }
  }, [agentsQuery.data]);

  // ---- UI state (local React) ----
  // `selectedAgent` mirrors the user-visible picker. Mirrors legacy
  // behavior exactly: `selectAgent(null)` clears the field, and the
  // initial default-selection runs once when the query first lands.
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  // Track whether we've ever picked a default selection so a later
  // refetch (e.g. after install) doesn't keep re-picking and override
  // a user-driven choice.
  const hasInitializedSelectionRef = useRef(false);

  // Translate React Query error/loading + the legacy "no agents" /
  // "no active agents" warnings into the string `error` field the
  // existing AgentContextType exposes. Keeps consumer-visible behavior
  // identical to the legacy implementation.
  const error: string | null = useMemo(() => {
    if (agentsQuery.isError) {
      return 'Failed to load agents. Please try refreshing the page.';
    }
    if (agentsQuery.isSuccess && agents.length === 0) {
      return 'No agents configured. Please contact your administrator.';
    }
    if (
      agentsQuery.isSuccess &&
      agents.length > 0 &&
      agents.every((a) => a.status !== 'active')
    ) {
      return 'No active agents available. Please contact your administrator.';
    }
    return null;
  }, [agentsQuery.isError, agentsQuery.isSuccess, agents]);

  // Default-selection effect: runs ONCE when the query first lands with
  // a non-empty agent list. Mirrors the legacy "first active agent or
  // admin-default" pick. Subsequent refetches don't re-pick.
  // Note: we set the "initialized" flag AFTER making a selection, not on
  // first invocation — otherwise the effect's first run sees the empty
  // `agents` mirror (set just one tick before in the sync effect above)
  // and exits without picking anything.
  useEffect(() => {
    if (hasInitializedSelectionRef.current) return;
    if (!agentsQuery.isSuccess) return;
    if (agents.length === 0) return;
    const activeAgents = agents.filter((a) => a.status === 'active');
    if (activeAgents.length === 0) {
      hasInitializedSelectionRef.current = true;
      return;
    }
    const defaultId = config.agentControl.defaultAgentId;
    const adminDefault = defaultId
      ? activeAgents.find((a) => a.id === defaultId)
      : undefined;
    setSelectedAgent(adminDefault ?? activeAgents[0]);
    hasInitializedSelectionRef.current = true;
  }, [agentsQuery.isSuccess, agents, config.agentControl.defaultAgentId]);

  // Mirror the active agent into error telemetry's ambient context so EVERY
  // reported error (file-attach rejections, image-encode failures, even
  // uncaught window.onerror events) records which agent was in play. This is
  // what lets the dashboard Errors page attribute an error to an agent —
  // e.g. distinguishing "images rejected because this agent isn't
  // vision-capable" from a genuine bug. Cleared when no agent is selected.
  useEffect(() => {
    setTelemetryContext(
      selectedAgent
        ? {
            agentId: selectedAgent.id,
            agentName: selectedAgent.name,
            agentFramework: selectedAgent.config?.framework,
            agentSupportsVision: agentSupportsVision(selectedAgent),
          }
        : null
    );
  }, [selectedAgent]);

  // ---- Mutations ----
  const installMutation = useInstallAgent();
  const importMutation = useImportAgent();
  const updateMutation = useUpdateAgent();
  const deleteMutation = useDeleteAgent();
  const duplicateMutation = useDuplicateAgent();
  const activateMutation = useActivateAgent();
  const deactivateMutation = useDeactivateAgent();
  const setDefaultMutation = useSetDefaultAgent();

  // ---- Optimistic mirror updates ----
  // Each mutation wrapper applies the change synchronously to the
  // local `agents` mirror AND to the React Query cache so both stay
  // in sync. The mirror is what consumers read; the cache is the
  // canonical store React Query refetches into.
  const queryClient = useQueryClient();
  const agentsQueryKey = useMemo(
    () => [...AGENTS_QUERY_KEY, user?.id ?? null] as const,
    [user?.id]
  );
  const patchAgents = useCallback(
    (patcher: (prev: Agent[]) => Agent[]) => {
      setAgents((prev) => {
        const next = patcher(prev);
        // Keep the cache aligned so a later refetch starts from the
        // right baseline and other observers (if any) see the update.
        queryClient.setQueryData<Agent[]>(agentsQueryKey, next);
        return next;
      });
    },
    [queryClient, agentsQueryKey]
  );

  // ---- Public API: thin wrappers that preserve legacy signatures ----
  const installAgent = useCallback(
    async (request: AgentInstallRequest): Promise<Agent> => {
      const agent = await installMutation.mutateAsync(request);
      // Mirror legacy setAgents((prev) => [...prev, agent]) so the
      // returned agent appears in the list synchronously without
      // waiting on the invalidate-triggered refetch.
      patchAgents((prev) => [...prev, agent]);
      // Legacy code polled the backend 1.5 s later to surface the
      // install→active transition. Preserve that behavior with a
      // fire-and-forget poll that fetches the post-transition row and
      // splices it into the cache.
      setTimeout(() => {
        void agentService.getAgent(agent.id).then((updated) => {
          if (updated) {
            patchAgents((prev) =>
              prev.map((a) => (a.id === updated.id ? updated : a))
            );
          }
        });
      }, 1500);
      return agent;
    },
    // Deps reference `installMutation.mutateAsync` (stable across renders
     // per React Query) rather than the full mutation result (new ref each
     // render). Same pattern for every mutation wrapper below.
     // eslint-disable-next-line react-hooks/exhaustive-deps
    [installMutation.mutateAsync, patchAgents]
  );

  const importAgent = useCallback(
    async (args: {
      payload: ExportEnvelope;
      mode: 'create' | 'update';
      targetAgentId?: string;
      secretOverrides?: Record<string, string>;
    }): Promise<Agent> => {
      const agent = await importMutation.mutateAsync(args);
      if (args.mode === 'create') {
        patchAgents((prev) => [...prev, agent]);
      } else {
        patchAgents((prev) =>
          prev.map((a) => (a.id === agent.id ? agent : a))
        );
        if (selectedAgent?.id === agent.id) {
          setSelectedAgent(agent);
        }
      }
      return agent;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [importMutation.mutateAsync, patchAgents, selectedAgent]
  );

  const updateAgent = useCallback(
    async (id: string, updates: Partial<Agent>): Promise<void> => {
      await updateMutation.mutateAsync({ id, updates });
      // Match legacy behavior: fetch the canonical post-update row and
      // splice it into the cache, plus update selectedAgent if it was
      // the one updated.
      const updated = await agentService.getAgent(id);
      if (updated) {
        patchAgents((prev) =>
          prev.map((a) => (a.id === id ? updated : a))
        );
        if (selectedAgent?.id === id) {
          setSelectedAgent(updated);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateMutation.mutateAsync, patchAgents, selectedAgent]
  );

  const deleteAgent = useCallback(
    async (id: string): Promise<void> => {
      // Legacy behavior: if we just deleted the selected agent, fall
      // back to the new isDefault or the first remaining agent. The
      // legacy code did this off the pre-delete list — we match that
      // for behavioral parity (the existing test asserts on the legacy
      // quirk of NOT filtering out the deleted agent here).
      const fallbackOnSelectedDelete =
        selectedAgent?.id === id
          ? (agents.find((a) => a.isDefault) ?? agents[0] ?? null)
          : undefined;
      await deleteMutation.mutateAsync(id);
      patchAgents((prev) => prev.filter((a) => a.id !== id));
      if (fallbackOnSelectedDelete !== undefined) {
        setSelectedAgent(fallbackOnSelectedDelete);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteMutation.mutateAsync, patchAgents, selectedAgent, agents]
  );

  const duplicateAgent = useCallback(
    async (id: string, name?: string): Promise<Agent> => {
      const agent = await duplicateMutation.mutateAsync({ id, name });
      patchAgents((prev) => [...prev, agent]);
      return agent;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [duplicateMutation.mutateAsync, patchAgents]
  );

  const activateAgent = useCallback(
    async (id: string): Promise<void> => {
      await activateMutation.mutateAsync(id);
      const updated = await agentService.getAgent(id);
      if (updated) {
        patchAgents((prev) =>
          prev.map((a) => (a.id === id ? updated : a))
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activateMutation.mutateAsync, patchAgents]
  );

  const deactivateAgent = useCallback(
    async (id: string): Promise<void> => {
      await deactivateMutation.mutateAsync(id);
      const updated = await agentService.getAgent(id);
      if (updated) {
        patchAgents((prev) =>
          prev.map((a) => (a.id === id ? updated : a))
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deactivateMutation.mutateAsync, patchAgents]
  );

  const setDefaultAgent = useCallback(
    async (id: string): Promise<void> => {
      await setDefaultMutation.mutateAsync(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDefaultMutation.mutateAsync]
  );

  const testAgent = useCallback(async (id: string): Promise<boolean> => {
    try {
      return await agentService.testConnection(id);
    } catch (err) {
      // Legacy code wrote the failure into a local `error` state. Now
      // that `error` is derived from the agents query, we can't push a
      // mutation-level failure into it without conflating concerns.
      // Log + return false instead — the boolean return value is the
      // contract callers use.
      console.error('[AgentContext] testAgent failed:', err);
      return false;
    }
  }, []);

  const refetchAgents = agentsQuery.refetch;
  const refreshAgents = useCallback(async (): Promise<void> => {
    await refetchAgents();
  }, [refetchAgents]);

  const selectAgent = useCallback(
    (agentId: string | null): void => {
      if (agentId === null) {
        setSelectedAgent(null);
        return;
      }
      const found = agents.find((a) => a.id === agentId) ?? null;
      setSelectedAgent(found);
    },
    [agents]
  );

  const value = useMemo<AgentContextType>(
    () => ({
      agents,
      selectedAgent,
      isLoading: agentsQuery.isLoading,
      error,
      installAgent,
      importAgent,
      updateAgent,
      deleteAgent,
      duplicateAgent,
      selectAgent,
      activateAgent,
      deactivateAgent,
      setDefaultAgent,
      testAgent,
      refreshAgents,
    }),
    [
      agents,
      selectedAgent,
      agentsQuery.isLoading,
      error,
      installAgent,
      importAgent,
      updateAgent,
      deleteAgent,
      duplicateAgent,
      selectAgent,
      activateAgent,
      deactivateAgent,
      setDefaultAgent,
      testAgent,
      refreshAgents,
    ]
  );

  return (
    <AgentContext.Provider value={value}>{children}</AgentContext.Provider>
  );
}

export function useAgent(): AgentContextType {
  const context = useContext(AgentContext);
  if (context === undefined) {
    throw new Error('useAgent must be used within an AgentProvider');
  }
  return context;
}
